registerComponent("sections/hero-redesign", function (component) {
/**
 * HERO — carrossel, transição do topo, revelação de entrada e parallax.
 *
 * O hero é um trilho full-bleed de três slides INDEPENDENTES: cada um carrega o
 * próprio backdrop de marca (gradiente · grafismo · luz), a própria arte e o
 * próprio texto, e os vizinhos assomam nas laterais. Este componente faz quatro
 * coisas:
 *
 *   1. roda o carrossel: setas, indicadores, avanço automático e arrasto — com
 *      a volta acontecendo na DIREÇÃO do gesto (ver `ir`);
 *   2. revela o hero (classe `is-in`), com rede de segurança (Asa.revelar),
 *      que só arma as animações de ambiente: o carregamento não tem entrada
 *      de texto desde 22/09, só a troca de slide;
 *   3. liga a transição do topo à rolagem — o percurso em que o card sobe até
 *      encostar no header e o monograma da assinatura encolhe até o lugar do
 *      logo na barra. Onde o navegador tem `animation-timeline: scroll()` o JS
 *      só mede o alcance (`--hero-alcance`) e marca `hero--nativo`; onde não
 *      tem, publica o progresso em `--hero-t` a cada quadro. Quem desenha é o
 *      CSS, nos dois casos (ADR 0043);
 *   4. desloca cada camada no scroll, com o fator declarado em `data-parallax`.
 *
 * A construção animada do monograma, que era a quinta coisa (ADRs 0054, 0055 e
 * 0060), saiu em 18/09 a pedido do cliente; as duas linhas da assinatura
 * deixaram de entrar em 22/09.
 *
 * Regra de ouro: sem JS, ou com `prefers-reduced-motion`, o hero fica no estado
 * final — completo, legível e parado. Nada aqui pode esconder conteúdo. Sem JS os
 * controles não aparecem (só ganham `--pronto` aqui), porque botão que não faz
 * nada é pior que botão ausente.
 *
 * @param {HTMLElement} component - elemento raiz do componente
 * @param {Object} params
 */

/* ═════════════════════════════════════════════════════════════ carrossel ═══ */

const carrossel = component.querySelector('.hero__carrossel');
const trilho = carrossel && carrossel.querySelector('.hero__track');
/* As duas pontas do trilho são CÓPIAS do último e do primeiro slide (ver a
   nota em _hero.html): elas existem para a volta ter direção e não entram na
   contagem, nos indicadores nem no foco. */
const slides = carrossel
    ? Array.from(carrossel.querySelectorAll('.hero__slide:not(.hero__slide--clone)'))
    : [];
const dots = carrossel ? Array.from(carrossel.querySelectorAll('.hero__dot')) : [];

if (carrossel && trilho && slides.length > 1) {
    /* A duração de cada slide mora no CSS (`--hero-slide-dur`), para o relógio do
       indicador e o cronômetro daqui nunca divergirem. */
    const duracao = (() => {
        const bruto = getComputedStyle(carrossel).getPropertyValue('--hero-slide-dur').trim();
        const n = parseFloat(bruto);

        if (!n) return 8000;
        return bruto.endsWith('ms') ? n : n * 1000;
    })();

    const autoplay = Asa.movimentoPermitido();

    let atual = 0;      /* o índice lógico: indicadores, aria, cronômetro */
    let cel = 0;        /* a célula que o trilho mostra; −1 e `n` são as cópias */
    let volta = null;   /* o índice para onde saltar quando a volta terminar */
    let timer = null;
    let inicio = 0;      /* quando o slide atual entrou (para o pause) */
    let restante = duracao;

    /* ⚠️ A PAUSA É UM CONJUNTO DE MOTIVOS, NÃO UM BOOLEANO (10/09).

       São cinco coisas que seguram o carrossel — o botão de pausa, o ponteiro
       sobre o card, o foco de teclado, a aba em segundo plano e o arrasto — e
       elas se sobrepõem: sair com o mouse do card com a aba oculta não pode
       destravar, nem soltar o dedo pode destravar o que o BOTÃO travou.

       Com um booleano o último a falar vencia. Agora cada um põe e tira o
       próprio motivo; o cronômetro só volta quando o conjunto esvazia. O timer
       continua sendo UM só (`timer`), e quem o remarca continua sendo só
       `armar`. */
    const motivos = new Set();
    const pausado = () => motivos.size > 0;

    /* --------------------------------------------------------------- estado --
       Slide fora de cena sai da árvore de acessibilidade E do foco: são três
       cópias do mesmo banner, e sem `inert` o Tab entraria nos botões
       invisíveis. */
    const clones = carrossel ? Array.from(carrossel.querySelectorAll('.hero__slide--clone')) : [];

    /* ------------------------------------------------- a arte do próximo slide
       Os slides 2 a 4 nascem `loading="lazy"` (PERF-007): baixá-los todos na
       carga custava 404 KB no celular, 44% do peso da página, por uma arte
       visível. Mas `lazy` puro faz o slide chegar em branco e a foto aparecer
       depois da rotação — então o VIZINHO é promovido a `eager` assim que o
       atual entra em cena, um ciclo inteiro (6s) antes de ser preciso.

       `loading = 'eager'` numa `<img>` que ainda não carregou dispara a busca na
       hora; nas que já carregaram é no-op.

       Fora do slide do LCP a arte nasce SEM endereço (`data-hero-src` e
       `data-hero-srcset`, ver o Blade): o `lazy` nativo não segurava os
       vizinhos, porque as espias das bordas estão na tela. `revelar` devolve o
       endereço — no slide atual (o indicador pode saltar para um slide que não
       era vizinho), nos dois vizinhos e nas cópias das pontas, que repetem a
       arte do primeiro e do último slide — a busca delas cai no cache.

       ⚠️ SÓ DEPOIS DO `load`. Na primeira pintura o slide atual é o do LCP, e
       os vizinhos promovidos aos ~180ms dividiam a banda com a imagem que decide
       a métrica (PERF-007). O `load` chega bem antes dos 6s da primeira rotação. */
    let esperandoCarga = false;

    const revelar = (img) => {
        if (!img.dataset.heroSrc) return;

        img.srcset = img.dataset.heroSrcset;
        img.src = img.dataset.heroSrc;
        delete img.dataset.heroSrcset;
        delete img.dataset.heroSrc;
    };

    const adiantarArte = (indice) => {
        const total = slides.length;

        if (!total) return;

        if (document.readyState !== 'complete') {
            if (!esperandoCarga) {
                esperandoCarga = true;
                window.addEventListener('load', () => adiantarArte(atual), { once: true });
            }

            return;
        }

        const vizinhos = [slides[indice], slides[(indice + 1) % total], slides[(indice - 1 + total) % total], ...clones];

        vizinhos.forEach((slide) => {
            if (!slide) return;

            slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
                img.loading = 'eager';
                img.fetchPriority = 'auto';
                revelar(img);
            });
        });
    };

    const pintar = () => {
        carrossel.style.setProperty('--hero-i', String(cel));

        /* A cópia em cena tem de crescer para a altura de slide ATIVO junto com a
           volta: ela nasce com `aria-hidden`, logo com o `scaleY` de inativo, e
           sem isto o card daria um pulo de 8 no instante do salto. A transição de
           0,7s é a mesma dos outros, então ela "acorda" enquanto entra, igual a
           um slide normal. Vale para as duas — só uma está em cena. */
        clones.forEach((clone) => clone.classList.toggle('hero__slide--indo', volta !== null));

        slides.forEach((slide, i) => {
            const ativo = i === atual;

            slide.inert = !ativo;

            if (ativo) slide.removeAttribute('aria-hidden');
            else slide.setAttribute('aria-hidden', 'true');
        });

        adiantarArte(atual);

        dots.forEach((dot, i) => {
            const ativo = i === atual;

            dot.classList.toggle('hero__dot--ativo', ativo);

            if (ativo) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
        });
    };

    /* ------------------------------------------------------------ cronômetro */
    const pararTimer = () => {
        if (timer) clearTimeout(timer);
        timer = null;
    };

    /* ⚠️ O CRONÔMETRO E O RELÓGIO DO INDICADOR SÃO UM ESTADO SÓ.
    
       Este `armar` remarcava o `setTimeout` sem consultar `pausado`. Com a pausa
       ligada — e ela ligava a cada clique de mouse numa seta, ver `focusin` lá
       embaixo — o slide continuava girando de 6 em 6s enquanto a barrinha do
       indicador ficava congelada em `animation-play-state: paused`. A barra não
       andava e o slide trocava mesmo assim.
    
       Pausado, o tempo que falta fica guardado e ninguém marca timer: quem
       remarca é o `retomar`. Assim é impossível o carrossel avançar com o
       relógio parado. */
    const armar = (ms) => {
        pararTimer();

        if (!autoplay) return;

        restante = ms;

        if (pausado()) return;

        inicio = performance.now();
        timer = setTimeout(() => ir(atual + 1, +1), ms);
    };

    /* -------------------------------------------------------------- a volta --
       O trilho é linear e a posição sai do índice. Enquanto o índice desse a
       volta (2 → 0), o deslocamento dava também: o carrossel andava meio segundo
       para TRÁS ao passar do último para o primeiro, no clique e a cada rotação
       do avanço automático.

       Agora quem manda na posição é `cel`, que pode sair da faixa dos slides por
       um passo: −1 é a cópia do último, `n` é a cópia do primeiro. A sequência da
       volta para a frente é
    
         anima  cel 2 → 3   (a cópia do primeiro entra pela direita)
         salta  cel 3 → 0   sem transição, entre dois quadros idênticos

       e a de trás é a mesma espelhada. `atual` vai para o valor final na hora,
       então indicadores e `aria` já contam o slide certo enquanto a animação
       acontece.

       `direcao` é +1 (seta da direita, swipe para a esquerda, avanço automático),
       −1 (o inverso) ou 0 (clique num indicador, que salta direto). */
    const semTransicao = () => {
        carrossel.classList.add('hero__carrossel--saltando');
        pintar();
        void trilho.offsetWidth;                 /* força o quadro sem transição */
        carrossel.classList.remove('hero__carrossel--saltando');
    };

    /* Se a transição estiver desligada (`prefers-reduced-motion`, ou o CSS de
       `--saltando` ainda aplicado), a cópia ficaria à vista até o salto. Aí não
       há volta a animar: vai direto para o índice. */
    const duracaoTransicao = () => parseFloat(getComputedStyle(trilho).transitionDuration) * 1000 || 0;
    const animado = () => duracaoTransicao() > 0;

    /* ------------------------------------------------------------ reentrada --
       A cada troca, o slide que entra REPETE a entrada do carregamento. Não é
       uma animação nova: os valores do "antes", a duração, a curva e o
       escalonamento são os mesmos do carregamento, declarados uma única vez no
       CSS (ver `#hero-antes` e `.hero__slide--entrando` em _hero.less). Aqui só
       se conduz o ciclo de três tempos: põe a classe (o slide pula para o
       "antes", sem animar, porque a classe zera a transição), força um quadro,
       tira a classe (a transição corre até o estado final).

       Entra só o CONTEÚDO — título, parágrafo e CTA. O fundo do tema e a arte
       da direita ficam parados e visíveis o tempo todo (ver `#hero-antes` em
       _hero.less), então o que viaja é um banner já montado, com o texto
       escondido por dentro.

       ⚠️ QUANDO A CLASSE SAI (04/09, segunda volta). Ela saía quando o slide
       terminava de CHEGAR — a viagem inteira do trilho, 760ms de espera antes de
       o primeiro elemento se mexer, e o CTA só começava 1,28s depois do clique,
       somado o escalonamento. Lia como lentidão, não como suavidade.

       Agora sai a `ESPERA_ENTRADA` da viagem. Com a curva do trilho
       (`cubic-bezier(0.22, 1, 0.36, 1)`, quase toda no começo) o card já
       percorreu perto de 70% do caminho nesse ponto: visualmente ele chegou, e o
       texto entra enquanto o resto assenta. A animação é a MESMA — mesma
       duração, mesma curva, mesmo escalonamento —, só não espera mais parada.

       É uma FRAÇÃO da duração lida do CSS, e não um número em ms, para não sair
       de sincronia se a transição do trilho mudar.

       Na volta o salto acontece no fim da viagem, bem depois desta soltura. Não
       há problema: a cópia e o slide real receberam a classe no mesmo instante e
       a perdem no mesmo instante, então os dois animam em passo — no quadro do
       salto mostram exatamente o mesmo estado.

       Cuidados que este fecho resolve:
       · troca rápida — `soltar()` limpa a marcação anterior antes de marcar a
         nova, então nenhum slide fica preso no estado "antes" nem acumula
         classe;
       · a volta — quem aparece primeiro é a CÓPIA da ponta, e só depois do
         salto o slide real. As duas mostram o mesmo quadro: se só o real
         recuasse, o texto da cópia entraria à vista e sumiria no salto. Por
         isso as cópias recuam junto;
       · `prefers-reduced-motion` — não arma nada (o CSS de lá também zera);
       · o ARRASTO (09/09, ADR 0068) — no celular ele revela o vizinho inteiro
         antes de qualquer `ir()`, e o texto dele aparecia pronto para sumir e
         reentrar. É o CSS que resolve: todo slide `aria-hidden` fica no "antes"
         (menos a cópia `--indo`, pela razão do item acima), então o que o dedo
         revela já vem sem texto e a marcação daqui só segura o estado até a
         soltura. */
    const ESPERA_ENTRADA = 0.25;   /* da viagem do trilho: ~175ms nos 0,7s do CSS */

    const reentrada = (() => {
        if (!Asa.movimentoPermitido()) return () => {};

        let marcados = [];
        let soltura = null;

        const soltar = () => {
            if (soltura) clearTimeout(soltura);
            soltura = null;

            marcados.forEach((slide) => slide.classList.remove('hero__slide--entrando'));
            marcados = [];
        };

        return (slide) => {
            soltar();

            if (!slide) return;

            marcados = [slide, ...clones];
            marcados.forEach((alvo) => alvo.classList.add('hero__slide--entrando'));

            void slide.offsetWidth;          /* fixa o "antes" antes de soltar */

            soltura = setTimeout(soltar, duracaoTransicao() * ESPERA_ENTRADA);
        };
    })();

    let timerVolta = null;

    const normalizar = () => {
        if (timerVolta) clearTimeout(timerVolta);
        timerVolta = null;

        if (volta === null) return;

        cel = volta;
        volta = null;
        semTransicao();
    };

    /* Tagueamento (plano v3.1): o slide que ficou ativo, uma vez por pageview,
       com o mecanismo que o trouxe. */
    const slideVisto = (i, metodo) => {
        const tema = (slides[i].className.match(/hero__slide--(\w+)/) || [])[1];

        Asa.trackUmaVez(`hero:${i}`, 'hero_slide_view', { index: i, slide_name: tema, method: metodo });
    };

    function ir(i, direcao = 0, metodo = 'auto') {
        const total = slides.length;

        /* Clique rápido: resolve a volta pendente antes de calcular o próximo
           passo, senão `cel` partiria de uma célula que já saiu de cena. */
        normalizar();

        const destino = ((i % total) + total) % total;

        /* Sem troca de slide não há o que reiniciar: a classe `--ativo` não muda
           de elemento, logo a animação do indicador NÃO recomeça, e remarcar o
           cronômetro aqui deixaria os dois fora de passo. Vale para o clique no
           indicador que já está ativo. */
        if (destino === atual) return;

        const daVolta = direcao !== 0 && destino !== atual + direcao;

        atual = destino;
        slideVisto(destino, metodo);

        if (daVolta && animado()) {
            cel = direcao > 0 ? total : -1;      /* a cópia, na direção do gesto */
            volta = destino;

            /* Rede: o `transitionend` não é garantido. Em aba de segundo plano a
               transição nem começa, e uma transição interrompida no meio também
               não emite o evento — nos dois casos a cópia ficaria na tela e
               `--hero-i` fora da faixa. O tempo vem do próprio CSS, com uma
               folga de 80ms. `normalizar` é idempotente, então quem chegar
               primeiro resolve. */
            timerVolta = setTimeout(normalizar, duracaoTransicao() + 80);
        } else {
            cel = destino;
        }

        /* `pintar()` primeiro: é ele que move o `aria-hidden`, e é do
           `aria-hidden` que dependem tanto a reentrada quanto a remontagem das
           animações de ambiente do slide que chega. */
        pintar();
        reentrada(slides[atual]);
        armar(duracao);
    }

    /* O salto acontece quando a transição do trilho termina. O `transitionend`
       do trilho também sobe pelos filhos, então filtra pelo alvo. */
    trilho.addEventListener('transitionend', (event) => {
        if (event.target === trilho && event.propertyName === 'transform') normalizar();
    });

    /* ------------------------------------------------------------- a pausa --
       O CONTROLE EXPLÍCITO é o que a WCAG 2.2.2 pede: conteúdo que se move
       sozinho por mais de 5s precisa de um mecanismo de pausa, e com 8s por
       slide isso deixou de ser opcional. O hover resolve só o mouse e o foco
       só o teclado; no toque não havia nenhum. O botão serve os três, é
       alcançável por Tab e diz o próprio estado.

       `aria-pressed` (e não um rótulo que troca sozinho): o botão é um
       interruptor, o rótulo descreve a AÇÃO e o estado vai no atributo.

       ⚠️ O BOTÃO ESTÁ DESLIGADO desde 10/09 à noite, a pedido do cliente:
       `pausaVisivel` no `_hero.html` não o emite. Este bloco continua inteiro e
       tolera a ausência (`botaoPausa` fica nulo, o `if` abaixo não arma nada e
       `marcarPausa()` sai na primeira linha), para religar ser uma palavra no
       template. Enquanto estiver desligado, o toque fica sem mecanismo de
       pausa — a nota no `_hero.html` explica o que isso custa. */
    const botaoPausa = carrossel.querySelector('.hero__pausa');

    const marcarPausa = () => {
        if (!botaoPausa) return;

        const travado = motivos.has('botao');

        botaoPausa.setAttribute('aria-pressed', travado ? 'true' : 'false');
        botaoPausa.classList.toggle('hero__pausa--travado', travado);
        botaoPausa.setAttribute('aria-label', travado ? 'Retomar a troca automática dos destaques' : 'Pausar a troca automática dos destaques');
    };

    /* O primeiro motivo a chegar congela o que falta do slide atual; os
       seguintes só entram no conjunto. Descontar o tempo decorrido mais de uma
       vez encurtaria o slide a cada motivo que se somasse — daí a guarda. */
    const pausar = (motivo) => {
        if (!autoplay || motivos.has(motivo)) return;

        if (!pausado()) {
            restante = Math.max(0, restante - (performance.now() - inicio));
            pararTimer();
            carrossel.classList.add('hero__carrossel--pausado');
        }

        motivos.add(motivo);
        marcarPausa();
    };

    /* Só o ÚLTIMO motivo a sair remarca o cronômetro, com o tempo que sobrou —
       nunca com a duração cheia. É o que faz a retomada continuar de onde
       parou, sem acelerar e sem trocar de slide antes da hora. */
    const retomar = (motivo) => {
        if (!autoplay || !motivos.delete(motivo)) return;

        if (!pausado()) {
            carrossel.classList.remove('hero__carrossel--pausado');
            armar(restante);
        }

        marcarPausa();
    };

    /* ---------------------------------------------------------------- eventos */
    const prev = carrossel.querySelector('.hero__seta--prev');
    const next = carrossel.querySelector('.hero__seta--next');

    if (prev) prev.addEventListener('click', () => ir(atual - 1, -1, 'arrow'));
    if (next) next.addEventListener('click', () => ir(atual + 1, +1, 'arrow'));

    /* O indicador salta direto para o slide: sem direção, sem volta. */
    dots.forEach((dot, i) => {
        dot.addEventListener('click', () => ir(i, 0, 'dot'));
    });

    /* O BOTÃO DE PAUSA. Trava e destrava; o motivo `botao` é o único que não
       sai sozinho — nem o ponteiro saindo do card, nem a aba voltando, nem uma
       troca manual de slide o soltam. Só o próprio botão. */
    if (botaoPausa) {
        botaoPausa.hidden = !autoplay;      /* sem autoplay não há o que pausar */

        botaoPausa.addEventListener('click', () => {
            if (motivos.has('botao')) retomar('botao');
            else pausar('botao');
        });

        marcarPausa();
    }

    /* O PONTEIRO sobre o card segura o slide — pedido de 10/09, que reverte a
       decisão de 01/09 ("o hero é banner de marca e o cursor cruza o card o
       tempo todo"). Com 8s por slide o argumento se inverteu: o tempo de
       leitura do banner passou a ser o tempo em que o cursor descansa nele.
       Sair do card devolve o cronômetro de onde parou.

       ⚠️ Só onde o hover EXISTE (`matchMedia`), e nunca de um ponteiro de
       toque: no celular o `pointerenter` do dedo dispara e o `pointerleave` só
       vem quando ele encosta noutro lugar — o carrossel ficaria parado sem
       ninguém ter pedido. Lá quem pausa é o botão. */
    {
        const temHover = matchMedia('(hover: hover)');

        carrossel.addEventListener('pointerenter', (event) => {
            if (temHover.matches && event.pointerType !== 'touch') pausar('ponteiro');
        });
        carrossel.addEventListener('pointerleave', () => retomar('ponteiro'));
    }

    /* Tabular para dentro do card segura o slide: quem navega por teclado não
       pode ter o conteúdo sob o foco trocando no meio do caminho. É a mesma
       ideia do botão, para quem não usa mouse.
    
       ⚠️ `:focus-visible`, e não `focusin` seco. Clicar num <button> com o mouse
       TAMBÉM dá foco, então o `focusin` seco pausava o carrossel a cada clique
       numa seta ou num indicador — e nada despausava, porque só o `focusout`
       faz isso. Quem clica está navegando, não lendo: só o foco de teclado
       (`:focus-visible`) segura o slide.
    
       O `try` é para navegador sem `:focus-visible`: ali vale a pausa, que é o
       lado seguro da WCAG. */
    carrossel.addEventListener('focusin', (event) => {
        let teclado = true;

        try { teclado = event.target.matches(':focus-visible'); } catch { /* sem suporte */ }

        if (teclado) pausar('foco');
    });
    carrossel.addEventListener('focusout', (event) => {
        if (!carrossel.contains(event.relatedTarget)) retomar('foco');
    });

    /* Aba em segundo plano não precisa girar carrossel. */
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) pausar('oculto');
        else retomar('oculto');
    });

    /* Setas do teclado, quando o foco está no carrossel. */
    carrossel.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { ir(atual - 1, -1, 'keyboard'); }
        else if (event.key === 'ArrowRight') { ir(atual + 1, +1, 'keyboard'); }
        else return;

        event.preventDefault();
    });

    /* ------------------------------------------------------------- arrasto --
       Swipe horizontal para trocar de slide. Sem biblioteca: o `embla-carousel`
       está no package.json mas não entra em bundle nenhum e não é usado em
       lugar algum do projeto, então trazê-lo aqui seria adicionar dependência
       de runtime, não reaproveitar o que existe. O trilho já é um `translateX`
       com transição — o arrasto só precisa somar um deslocamento a ele.

       ⚠️ VALE EM TODO PONTEIRO desde 04/09 — antes era só o grosso
       (dedo/caneta), com o argumento de que arrastar texto no mouse é seleção e
       que as setas já estão ali. O pedido agora é o inverso: click-and-drag
       também no desktop. As setas continuam, e o que era o risco do mouse virou
       duas linhas — `user-select: none` enquanto o gesto acontece (no CSS, na
       classe `--arrastando`) e o `dragstart` nativo barrado, senão a <img>
       gruda no cursor e mata o gesto no meio.

       Quem separa o gesto horizontal da rolagem vertical no TOQUE é o
       `touch-action: pan-y` do CSS: o navegador fica com o eixo Y e nos entrega
       o X. Por isso não há `preventDefault` em `pointermove` — a página
       continua rolando normalmente com o dedo sobre o card. No mouse a questão
       não existe (a roda rola a página de qualquer jeito), e a mesma guarda de
       intenção — 10px e |dx| > |dy| — vale para os dois.

       O limiar de troca é 12% da largura do slide, entre 40 e 100px: abaixo
       disso o trilho volta para o lugar, acima troca de slide. ⚠️ O TETO é do
       desktop: 12% de um card de 1352 dariam 162px de arrasto para trocar, que
       é o "exagero" que o pedido pede para evitar; no celular manda o piso. O
       `ir()` é o mesmo dos botões, então a volta do carrossel e o indicador
       ativo já vêm de graça. */
    {
        const LIMIAR = 0.12;
        const LIMIAR_MIN = 40;
        const LIMIAR_MAX = 100;

        let idPonteiro = null;
        let x0 = 0;
        let y0 = 0;
        let dx = 0;
        let horizontal = false;
        let arrastou = false;

        const largura = () => carrossel.querySelector('.hero__slide').offsetWidth || 1;

        const soltar = () => {
            idPonteiro = null;
            horizontal = false;
            carrossel.classList.remove('hero__carrossel--arrastando');
            carrossel.style.removeProperty('--hero-arrasto');
        };

        /* A <img> e o <a> são arrastáveis por padrão: sem isto, o mouse começa
           um arrasto nativo sobre a arte ou o CTA e o gesto morre no primeiro
           pixel. */
        carrossel.addEventListener('dragstart', (event) => event.preventDefault());

        carrossel.addEventListener('pointerdown', (event) => {
            if (idPonteiro !== null) return;

            /* Só o botão principal do mouse: o direito abre o menu de contexto e
               o do meio cola/rola, e nenhum dos dois é gesto de navegação. */
            if (event.pointerType === 'mouse' && event.button !== 0) return;

            idPonteiro = event.pointerId;
            x0 = event.clientX;
            y0 = event.clientY;
            dx = 0;
            horizontal = false;
            arrastou = false;
        }, { passive: true });

        carrossel.addEventListener('pointermove', (event) => {
            if (event.pointerId !== idPonteiro) return;

            dx = event.clientX - x0;

            /* Enquanto a intenção não estiver clara, não mexe em nada: um
               deslize vertical tem de continuar rolando a página. */
            if (!horizontal) {
                const dy = event.clientY - y0;

                if (Math.abs(dx) < 10 || Math.abs(dx) <= Math.abs(dy)) return;

                horizontal = true;
                arrastou = true;
                pausar('gesto');
                carrossel.classList.add('hero__carrossel--arrastando');
                carrossel.setPointerCapture(idPonteiro);

                /* O `user-select: none` da classe impede a seleção DAQUI para a
                   frente; o que o mouse já tenha pintado nos primeiros 10px sai
                   aqui. */
                const selecao = document.getSelection();

                if (selecao && !selecao.isCollapsed) selecao.removeAllRanges();
            }

            carrossel.style.setProperty('--hero-arrasto', `${dx}px`);
        }, { passive: true });

        const terminar = (event) => {
            if (event.pointerId !== idPonteiro) return;

            const era = horizontal;
            const passo = Math.min(LIMIAR_MAX, Math.max(LIMIAR_MIN, largura() * LIMIAR));

            soltar();

            if (!era) return;

            /* `retomar('gesto')` antes do `ir()` em qualquer caso: o gesto
               pausou o cronômetro, e sem isto o carrossel trocaria de slide com
               a classe `--pausado` ainda no ar — o relógio do indicador ficaria
               congelado enquanto o slide corre. O `armar()` de dentro do `ir()`
               recoloca o tempo cheio por cima.

               ⚠️ COM O MOTIVO. Sem ele o `delete` não acha nada, o `'gesto'`
               fica no conjunto para sempre e o carrossel morre pausado depois do
               primeiro swipe — foi o que a validação de celular pegou em 10/09.
               Se o botão tiver travado, o `'botao'` continua no conjunto e o
               carrossel segue parado, que é o certo. */
            retomar('gesto');

            if (dx <= -passo) ir(atual + 1, +1, 'swipe');
            else if (dx >= passo) ir(atual - 1, -1, 'swipe');
        };

        carrossel.addEventListener('pointerup', terminar);
        carrossel.addEventListener('pointercancel', terminar);

        /* Arrastar a partir do CTA não pode virar clique no CTA. A captura roda
           antes do handler do link, e `arrastou` só é verdadeiro depois de o
           gesto ter passado do limiar de 10px. */
        carrossel.addEventListener('click', (event) => {
            if (!arrastou) return;

            arrastou = false;
            event.preventDefault();
            event.stopPropagation();
        }, true);
    }

    carrossel.classList.add('hero__carrossel--pronto');

    /* O estado inicial não passa por `ir()`: ele agora ignora destino igual ao
       atual, e no arranque os dois são 0. */
    pintar();
    armar(duracao);
    if (slides.length) slideVisto(0, 'load');
}

/* ══════════════════════════════ transição do topo · parallax · revelação ═══ */

/* Fatores de deslocamento vivem no HTML (`data-parallax`), junto da camada a que
   pertencem. Hoje só a MODELO tem um (0.16): as camadas do `.hero__fundo` —
   gradiente, grafismo e luz — perderam o delas em 01/09, porque fundo que
   escorrega lê como o card inteiro deslizando. O laço abaixo não depende disso:
   ele monta a lista a partir do que houver no HTML.

   O DEGRAU vem de `Asa.desktop` (1025px, `@md + 1` de _variables.less): abaixo
   dele a modelo passa a preencher o card (ver @maxSm em _hero.less), a transição
   do topo não vale (ver @maxMd) e somar parallax a um enquadramento que já é
   `cover` só atrapalharia. `matchMedia`, e não `innerWidth`: é a mesma conta que
   o CSS faz — `innerWidth` conta a barra de rolagem e discorda no degrau. */

const assinatura = component.querySelector('.hero__assinatura');
const palco = component.querySelector('.hero__palco');

if (Asa.movimentoPermitido()) {
    Asa.revelar(component);

    /* -------------------------------------------------------------------------
       A TRANSIÇÃO DO TOPO (Figma 10586:28347).

       O header fica preso no alto, a assinatura da marca fica presa logo abaixo
       dele e o card do hero sobe por cima dela. Todo o desenho — a escala e a
       subida do monograma até o lugar do logo na barra, o esmaecimento das duas
       linhas — está no CSS. O JS entra de DUAS formas, conforme o navegador
       (ADR 0043, 05/09):

       · NATIVO (`animation-timeline: scroll()` — Safari 26, Chrome 115+): o CSS
         liga as propriedades à rolagem e as anima no compositor, em sincronia
         com o dedo. O JS só mede o ALCANCE, publica-o em `--hero-alcance` (px,
         no load e no resize) e marca `hero--nativo` na raiz — é a classe que
         liga o bloco `@supports` de _hero.less. No iPhone a versão por JS
         "travava": a rolagem corre a 120 Hz no compositor e o rAF a 60, um
         quadro atrás, e cada escrita invalidava o estilo do hero inteiro;

       · FALLBACK: publicar o progresso do percurso como `--hero-t`, de 0 a 1,
         na RAIZ DO COMPONENTE, a cada quadro de rolagem.

       ⚠️ EM `component`, e não no <html> (02/09). Escrita na raiz do documento, a
       variável invalidava o estilo de TODA a página a cada quadro de rolagem, e
       quem a lê está todo dentro do hero. O valor base para "sem JS" continua no
       `:root` de _tokens.less, e o `@maxMd { --hero-t: 0 }` de _hero.less é a
       rede do CSS — mas ele PERDE para um estilo inline em `component`, então o JS
       continua obrigado a escrever 0 abaixo do degrau, que é o que ele faz.

       Dois motivos para ser assim, e não com `transform` escrito daqui:
       · o valor base do CSS (0) é o estado INTEIRO. Sem JS, ou com o usuário
         pedindo menos movimento, nada chega a ser escondido nem encolhido;
       · o CSS continua sendo a única fonte da geometria, junto das medidas do
         arquivo de onde ela saiu.

       O ALCANCE é a distância que o card tem de subir para encostar no header:
       a altura da assinatura mais o vão dela até o card (78 + 53,57 = 131,57 na
       âncora de 1440 desde 18/09, com o monograma de 61 — o `offsetHeight`
       arredonda os 77,8 da caixa; eram 90 + 53 = 143
       com o de 67, 112 + 61 = 173 com ele a 64 do topo, e 160 + 32 = 192 antes
       do ADR 0083 — o monograma vaza 46 acima da caixa e não entra na conta, de
       propósito). Vem medido do DOM, e não escrito, porque os dois valores
       mudam por faixa.
       ---------------------------------------------------------------------- */
    /* Cada camada guarda as DUAS parcelas separadas — `dy` é a da rolagem, `mx`
       e `my` as do ponteiro — e um só escritor as soma. É o que permite os dois
       parallax coexistirem: houvesse um escritor por efeito, o segundo apagaria
       o `translate` do primeiro a cada quadro. */
    const camadas = Array.from(component.querySelectorAll('[data-parallax], [data-mouse]'))
        .map((camada) => ({
            elemento: camada,
            fator: parseFloat(camada.dataset.parallax) || 0,
            fmouse: parseFloat(camada.dataset.mouse) || 0,
            dy: 0,
            mx: 0,
            my: 0,
            escrito: '',
        }))
        .filter((camada) => camada.fator || camada.fmouse);

    /* O escritor único. Arredonda a 0,1px e só toca no estilo quando o valor
       muda de verdade — sem isso o laço do ponteiro sujaria o estilo das
       camadas a cada quadro mesmo paradas. */
    const escrever = (camada) => {
        const x = Math.round(camada.mx * 10) / 10;
        const y = Math.round((camada.dy + camada.my) * 10) / 10;
        const valor = (x || y) ? `${x}px ${y}px` : '';

        if (valor === camada.escrito) return;

        camada.escrito = valor;
        camada.elemento.style.translate = valor;
    };

    let alcance = 0;           /* o percurso da transição do topo */

    /* A linha do tempo de rolagem existe? `CSS.supports` responde pela
       propriedade, que é o que o `@supports` de _hero.less também testa. */
    const nativo = typeof CSS !== 'undefined'
        && typeof CSS.supports === 'function'
        && CSS.supports('animation-timeline: scroll()');

    const medir = () => {
        if (!assinatura || !carrossel || !palco) return;

        /* O vão assinatura → card é a margem do PALCO (a caixa alta que dá ao
           card o percurso para ficar preso), não do card. */
        const vao = parseFloat(getComputedStyle(palco).marginTop) || 0;

        alcance = assinatura.offsetHeight + vao;

        /* No modo nativo o alcance é o fim da `animation-range` — em px, que é
           a unidade em que ele foi medido. A classe só entra com o alcance já
           escrito: sem ele a faixa da animação seria a página inteira. */
        if (nativo && alcance) {
            component.style.setProperty('--hero-alcance', `${alcance}px`);
            component.classList.add('hero--nativo');
        }
    };

    /* -------------------------------------------------------------------------
       O passo.

       Um quadro por rajada de scroll (o agendador é o `Asa.aoRolar`), e cada
       valor só é escrito quando muda de verdade: `t` arredondado ao milésimo e o
       deslocamento das camadas a 0,1px, para não tremer nem sujar o estilo a cada
       quadro.

       As camadas já usam `transform` nas animações de ambiente, então o parallax
       vai em `translate` (propriedade independente) e os dois se somam sem
       conflito. O monograma da assinatura faz o mesmo no CSS.
       ---------------------------------------------------------------------- */
    let ultimoT = -1;
    let ultimoY = -1;

    const passo = (scroll = window.scrollY) => {
        /* O DEGRAU só vale para o parallax (ver abaixo). A transição do topo
           roda em toda largura desde 04/09 (ADR 0034): o frame de celular
           (10680:32570) pede o mesmo monograma encolhendo até a barra. */
        const ativo = Asa.desktop.matches;

        /* ---- a transição do topo (só o fallback; ver `nativo` acima) ---- */
        if (!nativo) {
            const t = alcance
                ? Math.round(Math.min(1, Math.max(0, scroll / alcance)) * 1000) / 1000
                : 0;

            if (t !== ultimoT) {
                ultimoT = t;
                component.style.setProperty('--hero-t', String(t));
            }
        }

        /* ---- o parallax das camadas ----
           O MESMO alcance da transição do topo, e não um palmo a mais.

           ⚠️ Ele ia até `alcance + a altura do card` (01/09): a ideia era que,
           preso no topo, o card ganhasse vida com a arte derivando por dentro.
           Só que é exatamente aí que o painel de Produtos começa a subir por
           cima dele — e, com o card parado, a arte descendo lia como o carrossel
           inteiro escorregando para baixo. Congelar o parallax no fim da subida
           deixa o card REALMENTE parado, que é o pedido. */
        if (!camadas.length) return;

        const y = ativo ? Math.max(0, Math.min(scroll, alcance)) : 0;

        if (y === ultimoY) return;
        ultimoY = y;

        for (const camada of camadas) {
            camada.dy = y * camada.fator;
            escrever(camada);
        }
    };

    window.addEventListener('resize', () => {
        medir();
        ultimoT = -1;    /* o alcance mudou: o valor de antes não vale mais */
        ultimoY = -1;
        passo();
    }, { passive: true });

    /* -------------------------------------------------------------------------
       O PARALLAX DE PONTEIRO (04/09) — o segundo nível de profundidade das duas
       bandeiras do slide Internacional.

       SOMA-SE ao da rolagem, não o substitui: cada camada guarda as duas
       parcelas e o `escrever` acima devolve `translate: mx  dy+my`. Parado o
       ponteiro, `mx` e `my` são 0 e o resultado é exatamente o de antes.

       O fator é o deslocamento em px na borda do card (`data-mouse` no HTML), e
       o ponteiro entra normalizado em −1..1 a partir do CENTRO do carrossel.

       Três decisões que fazem o movimento não ficar seco nem caro:
       · SUAVIZAÇÃO. O alvo é perseguido a 9% por quadro, não aplicado direto —
         é o que tira o "nervoso" do mouse e o que faz os elementos VOLTAREM
         suavemente ao lugar quando o ponteiro sai (o alvo vira 0 e o mesmo laço
         desacelera até lá);
       · o laço só corre enquanto há distância a percorrer, e se desliga
         sozinho ao assentar. Ponteiro parado não custa quadro nenhum;
       · o `getBoundingClientRect` é lido UMA vez por quadro, dentro do laço, e
         não no `pointermove` — que dispara mais vezes que o quadro e forçaria
         layout em cada um. O evento só guarda as coordenadas.

       Só com ponteiro FINO e capaz de pairar: no toque não há posição de mouse
       para seguir, e `pointermove` de dedo é o arrasto do carrossel. Tablet com
       mouse ou trackpad cai no mesmo `matchMedia` e ganha o efeito. Tudo isto
       está dentro do `Asa.movimentoPermitido()`, então quem pede menos
       movimento não arma nada.
       ---------------------------------------------------------------------- */
    const comMouse = camadas.filter((camada) => camada.fmouse);
    const ponteiroFino = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (comMouse.length && carrossel && ponteiroFino.matches) {
        const SUAVIZA = 0.09;
        const PARADO = 0.01;      /* px: abaixo disto, assenta e desliga o laço */

        let px = 0;
        let py = 0;
        let dentro = false;
        let quadro = null;

        const laco = () => {
            quadro = null;

            let alvoX = 0;
            let alvoY = 0;

            if (dentro) {
                const r = carrossel.getBoundingClientRect();

                if (r.width && r.height) {
                    alvoX = Math.max(-1, Math.min(1, (px - r.left - r.width / 2) / (r.width / 2)));
                    alvoY = Math.max(-1, Math.min(1, (py - r.top - r.height / 2) / (r.height / 2)));
                }
            }

            let andando = false;

            for (const camada of comMouse) {
                const faltaX = alvoX * camada.fmouse - camada.mx;
                const faltaY = alvoY * camada.fmouse - camada.my;

                if (Math.abs(faltaX) > PARADO || Math.abs(faltaY) > PARADO) {
                    camada.mx += faltaX * SUAVIZA;
                    camada.my += faltaY * SUAVIZA;
                    andando = true;
                } else {
                    camada.mx = alvoX * camada.fmouse;
                    camada.my = alvoY * camada.fmouse;
                }

                escrever(camada);
            }

            if (andando) quadro = requestAnimationFrame(laco);
        };

        const acordar = () => {
            if (!quadro) quadro = requestAnimationFrame(laco);
        };

        carrossel.addEventListener('pointermove', (event) => {
            if (event.pointerType !== 'mouse') return;

            px = event.clientX;
            py = event.clientY;
            dentro = true;
            acordar();
        }, { passive: true });

        /* Sair do card devolve as duas ao lugar pelo mesmo caminho suave. */
        carrossel.addEventListener('pointerleave', () => {
            dentro = false;
            acordar();
        }, { passive: true });
    }

    medir();

    /* Um `requestAnimationFrame` por rajada de rolagem, compartilhado com header,
       submenu e produtos (ver `aoRolar` em assets/.scripts/asa.js). Registra
       depois de `medir()`, porque `aoRolar` chama `passo` de imediato. */
    Asa.aoRolar(passo);
}

});
